import {
  SHButton,
  SHPopper,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { SHRichTextTooltip } from "@components/design-systems/sh-rich-text-tooltip";
import { Calendar2SVG } from "@components/svgs/calender2";
import { DateFormat3 } from "@constants/format";
import { useTheme } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useToggle, useUnmount } from "react-use";
import { useClickAwayRefs } from "@hooks/useClickAwayRefs";
import { MSTableResetButton } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/reset-button";
import { CheckSVG } from "@components/svgs";
import { SHMonthRangePicker, SHMonthRangePickerRef } from "@components/design-systems/sh-month-picker";
import { format } from "date-fns";
import { hexToRGBA, isSameMonthAndYear } from "@utils";
import {
  MSMonthRangePickerMessage,
  MSMonthRangeDefaultValues,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/month-range-picker/constants";
import { isEqual } from "lodash";
import { useModelScreener } from "@hooks/useModelScreener";
import { useSelector } from "react-redux";
import { RootState } from "@redux/store";

export const MSTableMonthRangePicker = React.memo(() => {
  const { palette } = useTheme();
  const {
    loadModelScreenerData: loadData,
    loadModelScreenerDataByDateRange: loadDataByDateRange,
  } = useModelScreener();

  const shouldHideToolbar = useSelector(
    (state: RootState) =>
      state.modelScreener.tableScrollProps.shouldHideToolbar,
  );
  const isLoadingDataByDateRange = useSelector(
    (state: RootState) => state.modelScreener.ui.isLoadingDataByDateRange,
  );

  // Refs
  const abortController = useRef<AbortController | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const monthRangePickerRef = useRef<SHMonthRangePickerRef>(null);

  // States
  const [startDate, setStartDate] = useState<Date | null>(
    MSMonthRangeDefaultValues.StartDate,
  );
  const [tempStartDate, setTempStartDate] = useState<Date | null>(startDate);
  const [endDate, setEndDate] = useState<Date | null>(
    MSMonthRangeDefaultValues.EndDate,
  );
  const [tempEndDate, setTempEndDate] = useState<Date | null>(endDate);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [open, toggleOpen] = useToggle(false);

  // Functions
  const handleOnReset = () => {
    monthRangePickerRef?.current?.reset(
      MSMonthRangeDefaultValues.StartDate,
      MSMonthRangeDefaultValues.EndDate,
    );
  };

  const handleOnApply = async () => {
    toggleOpen(false);
    abortController.current?.abort();

    if (
      isSameMonthAndYear(tempStartDate, startDate) &&
      isSameMonthAndYear(tempEndDate, endDate)
    ) {
      return;
    }

    setStartDate(tempStartDate);
    setEndDate(tempEndDate);

    await loadModelScreenerDataByDateRange({
      startDate: tempStartDate,
      endDate: tempEndDate,
    });
  };

  const loadModelScreenerDataByDateRange = async ({
    startDate,
    endDate,
  }: {
    startDate: Date | null;
    endDate: Date | null;
  }) => {
    abortController.current = new AbortController();

    if (
      isEqual(startDate, MSMonthRangeDefaultValues.StartDate) &&
      isEqual(endDate, MSMonthRangeDefaultValues.EndDate)
    ) {
      await loadData({
        signal: abortController.current?.signal,
        isReloadDataByDateRange: true,
      });
      return;
    }

    await loadDataByDateRange({
      fromDate: startDate,
      toDate: endDate,
      signal: abortController.current?.signal,
    });
  };

  const handleOnMonthRangePickerChange = (
    startDate: Date | null,
    endDate: Date | null,
  ) => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  };

  // Hooks
  useClickAwayRefs([wrapperRef], () => toggleOpen(false));

  useEffect(() => {
    shouldHideToolbar && toggleOpen(false);
  }, [shouldHideToolbar, toggleOpen]);

  const isResetButtonDisabled = useMemo(
    () =>
      isSameMonthAndYear(tempStartDate, MSMonthRangeDefaultValues.StartDate) &&
      isSameMonthAndYear(tempEndDate, MSMonthRangeDefaultValues.EndDate),
    [tempStartDate, tempEndDate],
  );

  useUnmount(() => abortController.current?.abort());

  return (
    <SHStack
      direction={"row"}
      gap={1.5}
      width={362}
      height={50}
      bgcolor={"#F2F1FF"}
      borderRadius={"32px"}
      alignItems={"center"}
      alignContent={"space-between"}
      paddingLeft={"21px"}
      paddingY={"7px"}
    >
      <SHTypography
        variant={"body1"}
        fontWeight={500}
        fontSize={14}
        color={palette.text.third}
      >
        Performance{" "}
        <SHRichTextTooltip htmlContent={MSMonthRangePickerMessage.Performance} />
      </SHTypography>

      <SHStack
        direction={"row"}
        width={210}
        height={"100%"}
        borderRadius={"32px"}
        alignItems={"center"}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          toggleOpen();
        }}
        sx={{ cursor: "pointer" }}
      >
        <SHStack
          direction={"row"}
          bgcolor={"white"}
          width={"46%"}
          height={"100%"}
          borderRadius={"32px"}
          alignItems={"center"}
          justifyContent={"space-around"}
          paddingX={0.5}
          paddingY={0.5}
        >
          <SHTypography color={"#4640DE"}>
            {!startDate ? "-" : format(startDate, DateFormat3)}
          </SHTypography>
          <Calendar2SVG width={"20px"} height={"20px"} />
        </SHStack>
        <SHStack width={"8%"} alignItems={"center"} textAlign={"center"}>
          -
        </SHStack>
        <SHStack
          direction={"row"}
          bgcolor={"white"}
          width={"46%"}
          height={"100%"}
          borderRadius={"32px"}
          alignItems={"center"}
          justifyContent={"space-around"}
          paddingX={0.5}
          paddingY={0.5}
        >
          <SHTypography color={"#4640DE"}>
            {!endDate ? "-" : format(endDate || new Date(), DateFormat3)}
          </SHTypography>
          <Calendar2SVG width={"20px"} height={"20px"} />
        </SHStack>
      </SHStack>

      <SHPopper
        anchorEl={anchorEl}
        open={open}
        placement="left-start"
        sx={{
          zIndex: 1,
          marginRight: "-235px !important",
          marginTop: "-10px !important",
          width: "fit-content",
        }}
      >
        <SHStack
          ref={wrapperRef}
          border={`1px solid ${hexToRGBA(palette.common.black, 0.12)}`}
          height={375}
          paddingX={"20px"}
          paddingY={"12px"}
          bgcolor={"white"}
          gap={1.5}
          sx={{
            filter: "drop-shadow(1px 1px 2px rgba(65, 73, 89, 0.25))",
          }}
        >
          <SHTypography variant="h2" fontWeight={600} paddingLeft={0.5}>
            Performance date range
          </SHTypography>

          <SHStack direction={"row"}>
            <SHMonthRangePicker
              ref={monthRangePickerRef}
              minYear={MSMonthRangeDefaultValues.MinYear}
              maxYear={MSMonthRangeDefaultValues.MaxYear}
              defaultStartDate={startDate}
              defaultEndDate={endDate}
              initialEndDate={MSMonthRangeDefaultValues.InitialEndDate}
              onChange={(value) =>
                handleOnMonthRangePickerChange(value[0], value[1])
              }
            />
          </SHStack>

          <SHStack
            direction={"row"}
            width={"100%"}
            height={40}
            alignItems={"center"}
            justifyContent={"end"}
            gap={2}
            sx={{ float: "right" }}
          >
            <MSTableResetButton
              disabled={isResetButtonDisabled}
              onClick={handleOnReset}
              height="32px"
            />
            <SHButton
              color="primary"
              size="medium"
              variant="contained"
              onClick={handleOnApply}
              isLoading={isLoadingDataByDateRange}
              startIcon={<CheckSVG stroke={"white"} />}
            >
              Apply
            </SHButton>
          </SHStack>
        </SHStack>
      </SHPopper>
    </SHStack>
  );
});
